import { useSearchParams } from 'react-router-dom'
import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { Icon } from '../components/common/Icon'
import { Reveal } from '../components/animation/Reveal'
import { EnquiryForm } from '../components/forms/EnquiryForm'
import { company, whatsappLink, mapEmbedUrl, mapLinkUrl } from '../data/company'
import { localBusinessSchema, breadcrumbSchema } from '../lib/seo'
import type { EnquiryType } from '../services/enquiryService'

export default function ContactPage() {
  const [params] = useSearchParams()
  const product = params.get('product') ?? ''
  const typeParam = params.get('type') as EnquiryType | null
  const defaultType: EnquiryType = product ? 'product' : typeParam ?? 'general'

  const contactItems = [
    { icon: 'MapPin', label: 'Visit us', value: company.address.full, href: mapLinkUrl },
    { icon: 'Phone', label: 'Call us', value: company.phoneDisplay, href: `tel:${company.phoneHref}` },
    { icon: 'Mail', label: 'Email us', value: company.email, href: `mailto:${company.email}` },
    { icon: 'MessageCircle', label: 'WhatsApp', value: 'Chat with us', href: whatsappLink('Hello GreenbayBio, I have an enquiry.') },
  ]

  return (
    <>
      <Seo
        title="Contact GreenbayBio — Product, Retailer & Distributor Enquiries"
        description={`Get in touch with ${company.legalName}. Call ${company.phoneDisplay}, email ${company.email}, or send a product, retailer or distributor enquiry.`}
        path="/contact"
        jsonLd={[localBusinessSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }])]}
      />
      <PageHeader
        eyebrow="Get in touch"
        title="Let’s talk about your crop."
        subtitle="Product questions, agronomic advice, or partnering with us as a retailer or distributor — we’re here to help."
      />

      <div className="container-x grid gap-10 py-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        {/* info */}
        <div>
          <Reveal className="grid gap-4 sm:grid-cols-2" stagger={0.1} y={20}>
            {contactItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="group rounded-xl border border-sand-200 bg-white p-6 shadow-soft transition-shadow hover:shadow-lift"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                  <Icon name={item.icon} className="h-5 w-5" />
                </span>
                <p className="mt-4 text-xs uppercase tracking-wide text-ink-500">{item.label}</p>
                <p className="mt-1 font-medium text-ink group-hover:text-brand-600">{item.value}</p>
              </a>
            ))}
          </Reveal>

          {company.businessHours && (
            <p className="mt-4 flex items-center gap-2 text-sm text-ink-500">
              <Icon name="Info" className="h-4 w-4" /> {company.businessHours}
            </p>
          )}

          <div className="mt-6 overflow-hidden rounded-xl border border-sand-200 shadow-soft">
            <iframe
              title={`Map to ${company.legalName}`}
              src={mapEmbedUrl}
              className="h-72 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="mt-2 text-xs text-ink-500">{company.whatsappNote}</p>
        </div>

        {/* form */}
        <div className="rounded-2xl border border-sand-200 bg-white p-7 shadow-soft md:p-9">
          <h2 className="font-display text-h3 font-semibold text-ink">Send an enquiry</h2>
          <p className="mt-1 text-sm text-ink-500">Fill in the form and we’ll get back to you.</p>
          <div className="mt-6">
            <EnquiryForm defaultType={defaultType} defaultProductSlug={product} />
          </div>
        </div>
      </div>
    </>
  )
}
